import React from 'react';
import { Edit2 } from 'lucide-react';
type MilestoneStatus = 'completed' | 'flagged' | 'skipped' | 'locked' | 'in_progress' | 'available';
interface LegalClause {
  id: string;
  number: number;
  title: string;
  status: MilestoneStatus;
  content: string | React.ReactNode;
  isRecentlyUpdated?: boolean;
  onEdit?: () => void;
}
interface LegalPreviewProps {
  testatorName: string;
  province: string;
  clauses: LegalClause[];
  isReviewMode?: boolean;
  isReady?: boolean;
}
export function LegalPreview({
  testatorName,
  province,
  clauses,
  isReviewMode = false,
  isReady = false
}: LegalPreviewProps) {
  return <div className="font-serif text-gray-800 leading-relaxed text-justify" data-id="element-984">
      <p className="mb-6" data-id="element-985">
        <strong data-id="element-986">I, {testatorName.toUpperCase() || '[FULL NAME]'}</strong>,
        currently residing in the Province of {province || '[PROVINCE]'},
        declare this to be my Last Will and Testament, and I revoke all prior
        Wills and Codicils made by me.
      </p>

      {clauses.map(clause => {
      if (clause.status === 'completed') {
        return <div key={clause.id} className={`relative ${clause.isRecentlyUpdated && !isReviewMode ? 'pl-4 border-l-4 border-violetBlue' : ''}`} data-id="element-987">
              {clause.isRecentlyUpdated && !isReviewMode && <span className="absolute -top-3 left-4 bg-violetBlue text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" data-id="element-988">
                  Updated
                </span>}
              <h5 className="font-bold mt-8 mb-4 uppercase text-sm tracking-wider flex items-center justify-between group" data-id="element-989">
                <span data-id="element-990">
                  {clause.number}. {clause.title}
                </span>
                {isReviewMode && clause.onEdit && <button onClick={clause.onEdit} className="opacity-0 group-hover:opacity-100 text-violetBlue text-xs font-sans font-medium flex items-center gap-1 transition-opacity" data-id="element-991">
                    <Edit2 className="w-3 h-3" data-id="element-992" /> Edit this section
                  </button>}
              </h5>
              <div data-id="element-993">{clause.content}</div>
            </div>;
      }
      if (clause.status === 'flagged') {
        return <div key={clause.id} className="bg-amber-50 p-4 border border-dashed border-amber-300 text-sm font-sans text-amber-800 my-8" data-id="element-994">
              [Clause {clause.number}: {clause.title} - Pending User Decision]
            </div>;
      }
      if (clause.status === 'skipped') {
        return <div key={clause.id} className="mt-8 text-sm italic text-gray-500" data-id="element-995">
              <h5 className="font-bold mb-2 uppercase tracking-wider text-gray-800 not-italic" data-id="element-996">
                {clause.number}. {clause.title}
              </h5>
              <p data-id="element-997">
                This clause has been omitted as it is not applicable to the
                testator's circumstances.
              </p>
            </div>;
      }
      return <div key={clause.id} className="opacity-30 blur-[2px] select-none mt-8" data-id="element-998">
            <h5 className="font-bold mb-4 uppercase text-sm tracking-wider" data-id="element-999">
              {clause.number}. {clause.title}
            </h5>
            <p data-id="element-1000">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </p>
          </div>;
    })}

      {/* General Provisions */}
      <div className={`mt-12 ${!isReady ? 'opacity-30 blur-[2px] select-none' : ''}`} data-id="element-1001">
        <h5 className="font-bold mb-4 uppercase text-sm tracking-wider" data-id="element-1002">
          7. General Provisions
        </h5>
        <p className="mb-4" data-id="element-1003">
          I grant my Trustee the power to sell, call in, and convert into money
          any part of my estate not consisting of money at such time or times,
          in such manner and upon such terms, and either for cash or credit or
          for part cash and part credit as my said Trustee may in their absolute
          discretion decide upon.
        </p>
        <p data-id="element-1004">
          I declare that the receipt of the person or persons to whom any
          payment or transfer is made under this my Will shall be a sufficient
          discharge to my Trustee who shall not be bound to see to the
          application thereof.
        </p>
      </div>

      {/* Signature Block */}
      <div className={`mt-20 ${!isReady ? 'opacity-30 blur-[2px] select-none' : ''}`} data-id="element-1005">
        <p className="mb-12" data-id="element-1006">
          IN WITNESS WHEREOF I have to this my Last Will and Testament set my
          hand this _____ day of ____________________, 20____.
        </p>
        <div className="flex justify-between items-end" data-id="element-1007">
          <div className="w-1/2 pr-8" data-id="element-1008">
            <div className="border-b border-gray-400 mb-2" data-id="element-1009"></div>
            <p className="text-sm" data-id="element-1010">Signature of Testator</p>
          </div>
          <div className="w-1/2 pl-8" data-id="element-1011">
            <div className="border-b border-gray-400 mb-2" data-id="element-1012"></div>
            <p className="text-sm" data-id="element-1013">Signature of Witness</p>
          </div>
        </div>
      </div>
    </div>;
}